const TOKEN_NAME = 'authToken';
const ORGANIZATION_ID_NAME = 'organizationId';
const RESET_PASSWORD_EMAIL_KEY = 'resetPasswordEmail';

export const setTokenLocalStorage = (token?: string) => {
  if (token) {
    window.localStorage.setItem(TOKEN_NAME, token);
  }
};

export const setTokenSessionStorage = (token?: string) => {
  if (token) {
    window.sessionStorage.setItem(TOKEN_NAME, token);
  }
};

export const getTokenLocalStorage = () => {
  return window.localStorage.getItem(TOKEN_NAME) || '';
};

export const getTokenSessionStorage = () => {
  return window.sessionStorage.getItem(TOKEN_NAME) || '';
};

export const removeToken = () => {
  // Remove from sessionStorage
  window.sessionStorage.removeItem(TOKEN_NAME);
  window.sessionStorage.removeItem(ORGANIZATION_ID_NAME);
  window.sessionStorage.removeItem('redirectUrl');

  // Remove from localStorage
  window.localStorage.removeItem(TOKEN_NAME);
  window.localStorage.removeItem(ORGANIZATION_ID_NAME);
  window.localStorage.removeItem(RESET_PASSWORD_EMAIL_KEY);
  window.localStorage.removeItem('_lr_id_');
  window.localStorage.removeItem('clients:lastViewedId');
  window.localStorage.removeItem('crews:lastViewedId');
  window.localStorage.removeItem('persist:locale');
  window.localStorage.removeItem('persist:root');
  window.localStorage.removeItem('projects:lastViewedId');
  window.localStorage.removeItem('subcontractors:lastViewedId');
  window.localStorage.removeItem('userRole');
};

// Synchronize tokens across storages after Redux rehydration to prevent early unauthenticated requests in new tabs
export const synchronizeTokensAfterRehydrate = (rememberMe: boolean) => {
  try {
    const localToken = getTokenLocalStorage();
    const sessionToken = getTokenSessionStorage();
    const selectedToken = rememberMe
      ? localToken || sessionToken
      : sessionToken || localToken;
    if (selectedToken) {
      // Write to both storages so any early reads succeed
      setTokenLocalStorage(selectedToken);
      setTokenSessionStorage(selectedToken);
    }
  } catch {
    // no-op: storage might be unavailable
  }
};

// Organization ID storage utilities
export const setOrganizationIdLocalStorage = (organizationId?: string) => {
  if (organizationId) {
    window.localStorage.setItem(ORGANIZATION_ID_NAME, organizationId);
  } else {
    window.localStorage.removeItem(ORGANIZATION_ID_NAME);
  }
};

export const setOrganizationIdSessionStorage = (organizationId?: string) => {
  if (organizationId) {
    window.sessionStorage.setItem(ORGANIZATION_ID_NAME, organizationId);
  } else {
    window.sessionStorage.removeItem(ORGANIZATION_ID_NAME);
  }
};

export const getOrganizationIdLocalStorage = () => {
  return window.localStorage.getItem(ORGANIZATION_ID_NAME) || '';
};

export const getOrganizationIdSessionStorage = () => {
  return window.sessionStorage.getItem(ORGANIZATION_ID_NAME) || '';
};

export const removeOrganizationId = () => {
  window.sessionStorage.removeItem(ORGANIZATION_ID_NAME);
  window.localStorage.removeItem(ORGANIZATION_ID_NAME);
};

// Get organization ID from any available storage (with fallback)
export const getOrganizationId = () => {
  return (
    window.localStorage.getItem(ORGANIZATION_ID_NAME) ||
    window.sessionStorage.getItem(ORGANIZATION_ID_NAME) ||
    ''
  );
};

export const synchronizeOrganizationIdAfterRehydrate = (rememberMe: boolean) => {
  try {
    const localOrgId = getOrganizationIdLocalStorage();
    const sessionOrgId = getOrganizationIdSessionStorage();
    const selectedOrgId = rememberMe
      ? localOrgId || sessionOrgId
      : sessionOrgId || localOrgId;
    if (selectedOrgId) {
      // Write to both storages so any early reads succeed
      setOrganizationIdLocalStorage(selectedOrgId);
      setOrganizationIdSessionStorage(selectedOrgId);
    }
  } catch {
    // no-op: storage might be unavailable
  }
};

// Reset password email storage utilities
// Uses localStorage so it persists when user opens the email link in a new tab
export const setResetPasswordEmail = (email: string) => {
  window.localStorage.setItem(RESET_PASSWORD_EMAIL_KEY, email);
};

export const getResetPasswordEmail = () => {
  return window.localStorage.getItem(RESET_PASSWORD_EMAIL_KEY) || '';
};

export const removeResetPasswordEmail = () => {
  window.localStorage.removeItem(RESET_PASSWORD_EMAIL_KEY);
};
